/**
 * 取引の内示ステータスの更新ロジックをまとめる
 */

import { businessForecastStatusType } from '@/coreDomainsModule/table';
import { initialOrder } from '../importData/initialData';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { businessForecastStatus } from '@/coreDomainsModule/entities/forecast';

/**
 * 取引の内示ステータスを管理
 * 1. 内示への対応を行なっていない(デフォルト)
 * 2. 取引の内示数量を仕入先内示へ追加
 * 3. 取引の内示数量を確定発注へ追加
 * 4. 1, 2のどちらへも追加済み
 *
 * 上記4パターンへの更新を一括管理
 */

/**
 * 取引の状態を管理する
 */
export class ForecastStatusManager {
  /**
   * 指定した全取引の内示ステータスを一括で指定した値に更新
   * @param businesses 内示のステータスを更新する取引
   * @param status このステータスに変更する
   * @returns DBへのセットが成功したか否か
   */
  private static bulkUpdateForecastState = async (businesses: IBusiness[], status: businessForecastStatus): Promise<IBusiness[]> => {
    const updatedBusinesses: IBusiness[] = businesses.map((business) => {
      return { ...business, businessForecastState: status };
    });
    return updatedBusinesses;
  };

  /**
   * 指定した取引を仕入先内示へ追加することをDBに記録する
   * @param businesses 仕入先内示へ追加する取引
   * @returns DBへのセットが成功したか否か
   */
  public static addSuppliersForecast = async (businesses: IBusiness[]): Promise<IBusiness[]> => {
    const updatedBusinesses: IBusiness[] = businesses.map((business) => {
      // 現在のステータスに応じて、仕入先内示への追加を行う
      switch (business.businessForecastState) {
        // 未対応の場合
        case undefined:
        case 'unsupported':
          return { ...business, businessForecastState: 'supplierForecastCompleted' };
        // 既に確定発注へ追加されている場合
        case 'orderCompleted':
          return { ...business, businessForecastState: 'completed' };
        default:
          break;
      }
      return business;
    });
    return updatedBusinesses;
  };

  /**
   * 指定した取引を確定発注へ追加することをDBに記録する
   * @param businesses 確定発注へ追加する取引
   * @returns DBへのセットが成功したか否か
   */
  public static addConfirmedOrder = async (businesses: IBusiness[]): Promise<IBusiness[]> => {
    const updatedBusinesses: IBusiness[] = businesses.map((business) => {
      // 現在のステータスに応じて、確定発注への追加を行う
      switch (business.businessForecastState) {
        // 未対応の場合
        case undefined:
        case 'unsupported':
          return { ...business, businessForecastState: 'orderCompleted' };
        // 確定発注を作成しない→やっぱり作成する！となった場合
        case 'notCreateOrder':
          return { ...business, businessForecastState: 'orderCompleted' };
        // 既に仕入先内示へ追加されている場合
        case 'supplierForecastCompleted':
          return { ...business, businessForecastState: 'completed' };
        default:
          break;
      }
      return business;
    });
    return updatedBusinesses;
  };

  /**
   * 指定した取引を「確定発注を作成しない(notCreateOrder)」というステータスにする
   * 「確定発注作成済み(orderCompleted)」の場合はこの関数を使用して書き返る想定では無い
   * TODO: 確定発注を作成するフローが完成したら、確定発注ページの「発注更新モーダル」で「はい」を押した時の処理をこちらに変更する
   * @param businesses 確定発注へ追加する取引
   * @returns ステータス更新済みの取引情報
   */
  public static forceUpdateOrderState = async (businesses: IBusiness[]): Promise<IBusiness[]> => {
    const updatedBusinesses: IBusiness[] = businesses.map((business) => {
      // ステータスの更新
      switch (business.businessForecastState) {
        // 未対応の場合
        case undefined:
        case 'unsupported':
          return { ...business, businessForecastState: 'notCreateOrder' };
        // 既に仕入先内示へ追加されている場合
        case 'supplierForecastCompleted':
          return { ...business, businessForecastState: 'completed' };
        default:
          break;
      }
      return business;
    });
    return updatedBusinesses;
  };

  /**
   * 仕入先内示への追加を取り消す
   * @param businesses 仕入先内示への追加を取り消す取引
   * @returns DBへのセットが成功したか否か
   */
  public static cancelAddedSuppliersForecast = async (businesses: IBusiness[]): Promise<IBusiness[]> => {
    const updatedBusinesses: IBusiness[] = businesses.map((business) => {
      // 現在のステータスに応じて、仕入先内示への追加のみ取り消す
      switch (business.businessForecastState) {
        case 'supplierForecastCompleted':
          return { ...business, businessForecastState: 'unsupported' };
        case 'completed':
          return { ...business, businessForecastState: 'orderCompleted' };
        default:
          break;
      }
      return business;
    });
    return updatedBusinesses;
  };

  /**
   * 確定発注への追加を取り消す
   * @param businesses 確定発注への追加を取り消す取引
   * @returns DBへのセットが成功したか否か
   */
  public static cancelAddedConfirmedOrder = async (businesses: IBusiness[]): Promise<IBusiness[]> => {
    const updatedBusinesses: IBusiness[] = businesses.map((business) => {
      // 発注情報が一つも残っていない場合にステータス書き換えを実行する
      if (business.recommend.length === 0) {
        // 現在のステータスに応じて、確定発注への追加のみ取り消す
        switch (business.businessForecastState) {
          case 'orderCompleted':
            return { ...business, ...initialOrder, businessForecastState: 'unsupported' };
          case 'completed':
            return { ...business, ...initialOrder, businessForecastState: 'supplierForecastCompleted' };
          default:
            break;
        }
      }
      return business;
    });
    return updatedBusinesses;
  };

  /**
   * 内示情報の変更処理を全て取り消す
   * @param businesses 内示情報の変更処理を取り消す取引
   * @returns DBへのセットが成功したか否か
   */
  public static cancelAllForecastRelatedProcesses = async (businesses: IBusiness[]): Promise<IBusiness[]> => {
    return await this.bulkUpdateForecastState(businesses, 'unsupported');
  };

  /**
   * 発注ステータスor仕入先内示ステータスについて、処理が完了しているのかどうかをフラグで返す
   * @param type 発注ステータスor仕入先内示ステータス
   * @param status 現在セットされているステータスの値
   * @returns 処理が完了しているかどうか
   */
  public static isCompleted = (type: businessForecastStatusType, status: businessForecastStatus): boolean => {
    switch (type) {
      case 'orderStatus':
        return status === 'orderCompleted' || status === 'completed';
      case 'supplierForecastStatus':
        return status === 'supplierForecastCompleted' || status === 'completed';
    }
  };
}
